import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getNameIncidentsTypes, translate } from "../../../../../utils/Common";
import Spinner from "../../../../../components/Spinner";
import { IoWarningOutline } from "react-icons/io5";
import { useForm } from "react-hook-form/dist/index.ie11";
import PlatformSearchList from "../../../../../components/PlatformSearchList";
import { useIncidents } from "../../../../../hooks/useIncidents";
import { BiReset } from "react-icons/bi";
import Select from "../../../../../components/common/Select";
import Button from "../../../../../components/common/Button";
import DatePicker from "../../../../../components/DatePicker";
import { SegmentedControl } from "@/components/ui/segmented-control";
import styles from "../Packages/Charts.module.css";
import ignoreAbort from '../../../../../utils/ignoreAbort';
import IncidentKpiRow from "./IncidentKpiRow";
import IncidentClosedByUser from "./IncidentClosedByUser";
import {
  CUSTOM_PRESET,
  INCIDENT_PERIOD_PRESETS,
  MAX_RANGE_DAYS,
  formatBucket,
  formatRange,
  getPresetRange,
  loadPreset,
  savePreset,
  toApiDate,
} from "./incidentPeriodPresets";

const DAY_MS = 24 * 60 * 60 * 1000;

const IncidentGeneralChart = (props) => {
  const { yearSelect, canReadFilters = true } = props;
  const { getResolutionStats, typesIncidents, getTypes } = useIncidents();
  const { register, errors, setValue } = useForm();

  const [stats, setStats] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [platformSelect, setPlatformSelect] = useState(null);
  const [typeIncident, setTypeIncident] = useState(0);
  const [typeListIncidents, setTypeListIncidents] = useState([]);
  const [priorityIncident, setPriorityIncident] = useState(0);
  const [preset, setPreset] = useState(loadPreset);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  // El rango sale del favorito elegido; "Personalizado" usa las dos fechas del picker
  const range = useMemo(() => {
    if (preset !== CUSTOM_PRESET) return getPresetRange(preset, { year: yearSelect });
    if (!customStart || !customEnd) return null;
    return { dateFrom: toApiDate(customStart), dateTo: toApiDate(customEnd) };
  }, [preset, yearSelect, customStart, customEnd]);

  const rangeTooLong = range && (new Date(range.dateTo) - new Date(range.dateFrom)) / DAY_MS + 1 > MAX_RANGE_DAYS;

  // Al cambiar el año del header se pasa al favorito "Año" para que el selector siga mandando
  useEffect(() => {
    if (yearSelect && Number(yearSelect) !== new Date().getFullYear()) setPreset("year");
  }, [yearSelect]);

  useEffect(() => {
    const abortController = new AbortController();

    const handleTypes = async () => {
      const response = await getTypes(abortController.signal);
      setTypeListIncidents(response);
    };

    if (!typesIncidents) handleTypes().catch(ignoreAbort);
    else setTypeListIncidents(typesIncidents);

    return () => abortController.abort();
  }, [getTypes, typesIncidents]);

  useEffect(() => {
    if (!range || rangeTooLong) return undefined;
    const abortController = new AbortController();

    const getData = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await getResolutionStats(abortController.signal, {
          ...range,
          lockerId: platformSelect?.boxitLocationId ? Number(platformSelect.boxitLocationId) : null,
          typeIncident: typeIncident > 0 ? Number(typeIncident) : null,
          priorityIncident: priorityIncident > 0 ? Number(priorityIncident) : null,
        });
        setStats(result);
      } catch (error) {
        if (error?.name === "AbortError") return;
        setStats(null);
        setHasError(true);
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    };

    getData();
    return () => abortController.abort();
  }, [range, rangeTooLong, platformSelect, typeIncident, priorityIncident, getResolutionStats]);

  const handlePresetChange = (key) => {
    setPreset(key);
    savePreset(key);
  };

  const resetFilters = () => {
    setTypeIncident(0);
    setPriorityIncident(0);
    setPlatformSelect(null);
  };

  const hasFilters = typeIncident > 0 || priorityIncident > 0 || platformSelect !== null;

  const presetOptions = [
    ...INCIDENT_PERIOD_PRESETS.map((item) => ({
      value: item.key,
      label: item.key === "year" ? `${translate(item.label)} ${yearSelect || new Date().getFullYear()}` : translate(item.label),
    })),
    { value: CUSTOM_PRESET, label: translate("dashboard.insights.incidents.presets.custom") },
  ];

  const typeIncidentOptions = [
    { value: "0", label: translate("global.all") },
    ...typeListIncidents.map((type) => ({
      value: String(type?.id),
      label: getNameIncidentsTypes(type?.id),
    })),
  ];

  const priorityOptions = [
    { value: "0", label: translate("global.all") },
    { value: "1", label: translate("global.incidents.priority.high") },
    { value: "2", label: translate("global.incidents.priority.medium") },
    { value: "3", label: translate("global.incidents.priority.low") },
  ];

  const chartData = (stats?.series || []).map((item) => ({
    label: formatBucket(item.bucket, stats.granularity),
    created: item.created,
    closed: item.closed,
  }));
  const isEmpty = stats && !stats.summary?.created && !stats.summary?.closed && !stats.summary?.open;

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeaderRow}>
        <h6 className={styles.chartTitle}>
          {isLoading ? (
            <span><Spinner type={1} /></span>
          ) : (
            <IoWarningOutline size={20} aria-hidden />
          )}{" "}
          {translate("global.incidentsStatistics")}
          {range && (
            <>
              {" "}-{" "}
              <span className={styles.chartTitleAccent} style={{ color: "var(--color-warning)" }}>
                {formatRange(range.dateFrom, range.dateTo)}
              </span>
            </>
          )}
        </h6>
      </div>

      <hr className={styles.chartDivider} />

      {canReadFilters && (
        <div className="mb-3 flex w-full min-w-0 flex-col gap-3">
          {/* Favoritos: períodos predefinidos en código */}
          <div className="-mx-1 max-w-full overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
            <SegmentedControl
              name="incidentPeriodPreset"
              options={presetOptions}
              value={preset}
              onChange={handlePresetChange}
            />
          </div>

          {preset === CUSTOM_PRESET && (
            <div className="grid w-full max-w-md grid-cols-1 gap-x-3 sm:grid-cols-2">
              <DatePicker
                selectedDate={customStart}
                onChangeDate={(date, which) => (which === "end" ? setCustomEnd(null) : setCustomStart(date))}
                placeholder={translate("dashboard.insights.incidents.presets.from")}
                endDate={customEnd}
                maxRangeDays={MAX_RANGE_DAYS - 1}
              />
              <DatePicker
                selectedDate={customEnd}
                onChangeDate={setCustomEnd}
                placeholder={translate("dashboard.insights.incidents.presets.to")}
                startDate={customStart}
                endDate={customEnd}
                disabled={!customStart}
                maxRangeDays={MAX_RANGE_DAYS - 1}
              />
            </div>
          )}

          <div className={styles.chartFilters}>
            <div className={styles.filterGroup}>
              <label htmlFor="inputTypeIncident" className={styles.filterLabel}>
                {translate("global.typeIncident")}
              </label>
              <Select
                id="inputTypeIncident"
                value={String(typeIncident)}
                onChange={(e) => setTypeIncident(Number(e.target.value))}
                options={typeIncidentOptions}
                size="sm"
              />
            </div>
            <div className={styles.filterGroup}>
              <label htmlFor="inputPriorityIncident" className={styles.filterLabel}>
                {translate("global.priority")}
              </label>
              <Select
                id="inputPriorityIncident"
                value={String(priorityIncident)}
                onChange={(e) => setPriorityIncident(Number(e.target.value))}
                options={priorityOptions}
                size="sm"
              />
            </div>
            <div className={`${styles.selectWrapper} md:w-[220px]`}>
              <label htmlFor="inputPlatform" className={styles.filterLabel}>
                {translate("global.selectALocker")}
              </label>
              <PlatformSearchList
                register={register}
                errors={errors}
                setPlatformSelect={setPlatformSelect}
                platfSelect={platformSelect}
                valueId="plataforma"
                readOnly={false}
                setValue={setValue}
              />
            </div>
            {hasFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                leftIcon={<BiReset size={16} />}
                aria-label={translate("global.reset")}
              >
                {translate("global.reset")}
              </Button>
            )}
          </div>
        </div>
      )}

      {!range ? (
        <div className={styles.noDataCard}>{translate("dashboard.insights.incidents.presets.pickRange")}</div>
      ) : rangeTooLong ? (
        <div className={styles.noDataCard}>{translate("dashboard.insights.incidents.presets.rangeTooLong")}</div>
      ) : hasError ? (
        <div className={styles.noDataCard}>{translate("dashboard.insights.incidents.kpi.error")}</div>
      ) : stats ? (
        <>
          <IncidentKpiRow summary={stats.summary} previous={stats.previous} loading={false} />

          {isEmpty ? (
            <div className={styles.noDataCard}>{translate("global.noDataToView")}</div>
          ) : (
            <>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 8, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="label" stroke="var(--text-secondary)" fontSize={12} />
                    <YAxis allowDecimals={false} width={32} stroke="var(--text-secondary)" fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="created"
                      fill="var(--color-primary)"
                      maxBarSize={32}
                      radius={[4, 4, 0, 0]}
                      name={translate("dashboard.insights.incidents.kpi.created")}
                    />
                    <Bar
                      dataKey="closed"
                      fill="var(--color-success)"
                      maxBarSize={32}
                      radius={[4, 4, 0, 0]}
                      name={translate("dashboard.insights.incidents.kpi.closed")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <IncidentClosedByUser users={stats.byUser} totalClosed={stats.summary?.closed} />
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default IncidentGeneralChart;
